import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  ApiResponse,
  PaginatedResponse,
  User,
  Property,
  Transaction,
  StakingPosition,
  Portfolio,
  PortfolioPosition,
  PropertyFilters,
  TransactionFilters,
  LoginForm
} from '../types';
import config, { API_ENDPOINTS } from '../config';
import { authNav, clearAuthStorage, isCredentialPath, isLoginSurface, loginRouteFor } from '../config/authRoutes';
import { toast } from 'react-hot-toast';
import { READ_ONLY_CODE, claimOf, exitSignInAs, readOnlyMessage } from './impersonation';

/** Read-only "Sign in as": one plain sentence on screen, however many requests were refused at once. */
const READ_ONLY_TOAST_ID = 'impersonation-read-only';
function notifyReadOnly(message: string) {
  try { toast(message, { id: READ_ONLY_TOAST_ID }); } catch { /* no toaster mounted */ }
}

let returning = false;
/** The sign-in-as ended under us (expired or revoked): back to the administrator, never a refresh. */
function returnFromSignInAs() {
  if (returning) return;
  returning = true;
  void exitSignInAs().then((r) => {
    window.location.assign(r.ok ? '/admin/users' : loginRouteFor(window.location.pathname));
  });
}

// Create axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and sandbox mode header
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach sandbox payments header when admin has toggled it on
    const sandboxPayments = localStorage.getItem('sandboxPayments');
    if (sandboxPayments === 'true' && config.headers) {
      config.headers['X-Sandbox-Mode'] = 'true';
    }
    // Attach private invest access token for non-accredited investor flows
    const investAccessToken = sessionStorage.getItem('investAccessToken');
    if (investAccessToken && config.headers) {
      config.headers['X-Invest-Access-Token'] = investAccessToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
let isRefreshing = false;
let failedQueue: any[] = [];

/** Send the user to the sign-in that belongs to the workspace they are in. */
const redirectToSignIn = () => {
  const here = window.location.pathname;
  if (!isLoginSurface(here)) {
    authNav.go(loginRouteFor(here));
  }
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle common errors
    if (error.response) {
      const { status } = error.response;

      // Read-only "Sign in as" (2026-10-26): a refused write is a plain sentence, not a raw error. The message is
      // rewritten on the response, so apiRequest's {success:false, message} carries it to the caller too.
      if (status === 403 && error.response.data?.code === READ_ONLY_CODE) {
        const message = error.response.data?.message || readOnlyMessage();
        error.response.data = { ...error.response.data, message };
        notifyReadOnly(message);
        return Promise.reject(error);
      }
      // An impersonation session is never refreshed: when it is refused, return to the administrator.
      if (status === 401 && claimOf(localStorage.getItem('authToken')) && !isCredentialPath(originalRequest?.url)) {
        returnFromSignInAs();
        return Promise.reject(error);
      }

      /**
       * Refresh only when this is an authenticated-resource 401.
       *
       * Three independent conditions, deliberately. Each one alone would have
       * prevented D-1, and each is a different kind of mistake:
       *
       *  - `isCredentialPath` — a sign-in has no session to refresh. A 401 here
       *    means the credentials are wrong, and it must reach the caller as a
       *    401 rather than as whatever `/auth/refresh` answers.
       *  - `hasRefreshCredential` — never ask the server to refresh nothing.
       *    Signed out, the old code posted a null token and spent a second
       *    request against the credential rate limiter for every wrong
       *    password. Behind one office NAT that halves the shared budget.
       *  - `_retry` — the pre-existing guard against refreshing in a loop.
       *
       * If a later change removes the path exclusion, the token check still
       * stops the null refresh; if the token handling regresses, the path
       * exclusion still stops sign-in from refreshing. Defence in depth is the
       * point, not redundancy.
       */
      const credentialRequest = isCredentialPath(originalRequest?.url);
      const hasRefreshCredential = !!localStorage.getItem('refreshToken');

      if (status === 401 && !originalRequest._retry && !credentialRequest && !hasRefreshCredential) {
        // A protected resource refused, and there is nothing to refresh with.
        // Clear the dead session and send the user to the sign-in that belongs
        // to where they are — but make no refresh request, and hand back the
        // ORIGINAL error so the caller still sees the 401 it asked about.
        clearAuthStorage();
        redirectToSignIn();
        return Promise.reject(error);
      }

      // Handle 401 with token refresh
      if (status === 401 && !originalRequest._retry && !credentialRequest && hasRefreshCredential) {
        if (isRefreshing) {
          // If token is already being refreshed, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['Authorization'] = 'Bearer ' + token;
              return apiClient(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Attempt to refresh the token
          const storedRefresh = localStorage.getItem('refreshToken');
          const response = await axios.post(
            `${config.apiBaseUrl}/auth/refresh`,
            { refreshToken: storedRefresh },
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
              }
            }
          );

          const rd = response.data;
          const newToken = rd.data?.token || rd.accessToken;
          const newRefresh = rd.data?.refreshToken || rd.refreshToken;
          if (rd.success && newToken) {
            localStorage.setItem('authToken', newToken);
            if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
            
            // Store new expiry time
            try {
              const { jwtDecode } = await import('jwt-decode');
              const decoded: any = jwtDecode(newToken);
              const expiryTime = decoded.exp * 1000;
              localStorage.setItem('tokenExpiry', expiryTime.toString());
            } catch (e) {
              console.warn('Failed to decode refreshed token:', e);
            }

            // Update the original request with new token
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            
            // Process queued requests
            processQueue(null, newToken);
            
            // Retry the original request
            return apiClient(originalRequest);
          } else {
            throw new Error('Token refresh failed');
          }
        } catch (refreshError) {
          // Token refresh failed - clear auth and redirect
          processQueue(refreshError, null);
          clearAuthStorage();
          redirectToSignIn();

          // The ORIGINAL error, not `refreshError`. The caller asked about the
          // protected resource; handing back the refresh call's failure
          // substitutes an unrelated endpoint's status for the real one, which
          // is precisely how a 401 came to be reported as a 400.
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }

      // Other status codes are handled by components via apiRequest response
      // No global toasts - components check response.success and display response.message inline
    }

    // Network and other errors are also handled by components
    return Promise.reject(error);
  }
);

// Generic API function
export async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  try {
    const response = await apiClient.request<ApiResponse<T>>({
      method,
      url,
      data,
      ...config,
    });
    return response.data;
  } catch (error: any) {
    // Return a structured error response. The status travels with it: a 429
    // and a 401 are different facts, and a caller that cannot tell them apart
    // reports a rate limit as a wrong password and then retries into it.
    //
    // The decision code travels for the same reason. It is read from
    // `data.code` and deliberately NOT from `data.error`: the global error
    // handler puts its machine code in `error` (INTERNAL_ERROR, RATE_LIMITED,
    // VALIDATION_ERROR) while the authorization and workspace middleware put
    // human prose there, and callers render `error` directly as user copy.
    // Forwarding it would put the literal word INTERNAL_ERROR on screen.
    //
    // There is no separate "offline" flag, and there should not be: a network
    // failure has no response at all, so both `status` and `code` are
    // undefined. That absence IS the signal — `status === undefined` means the
    // server was never reached, which is a different sentence from a refusal.
    // A 429 says when to try again (2026-10-05): the Retry-After header first,
    // else the body's own figure. Absent on every other failure.
    const retryHeader = Number(error.response?.headers?.['retry-after']);
    const retryBody = Number(error.response?.data?.retryAfterSeconds);
    const retryAfterSeconds = error.response?.status === 429
      ? (Number.isFinite(retryHeader) && retryHeader > 0 ? retryHeader : Number.isFinite(retryBody) && retryBody > 0 ? retryBody : undefined)
      : undefined;
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Request failed',
      message: error.response?.data?.message || 'Request failed',
      status: error.response?.status,
      code: error.response?.data?.code,
      detail: error.response?.data?.detail,
      ...(retryAfterSeconds ? { retryAfterSeconds } : {}),
    };
  }
}

/**
 * Download a binary response (QR images, generated PDFs) through the shared
 * client.
 *
 * Callers used to reach for raw `fetch` for these, because `apiRequest` types
 * its result as JSON. That quietly opts out of the request/response
 * interceptors above — most importantly the 401 handler that refreshes an
 * expired access token and retries. The symptom is specific and confusing: the
 * whole page keeps working while one download button fails, because every
 * other call silently recovers and that one does not.
 *
 * Throws on failure so callers can distinguish "no file" from an empty file.
 */
export async function apiDownload(url: string): Promise<Blob> {
  const response = await apiClient.get(url, { responseType: 'blob' });
  return response.data as Blob;
}

/**
 * Upload a file through the shared client.
 *
 * The `Content-Type: application/json` default on `apiClient` above is not
 * inert for multipart requests — it actively destroys them. axios's
 * transformRequest reads the header before it looks at the body:
 *
 *     const hasJSONContentType = contentType.indexOf('application/json') > -1;
 *     if (isFormData) {
 *       return hasJSONContentType ? JSON.stringify(formDataToJSON(data)) : data;
 *     }
 *                             — axios/lib/defaults/index.js:46,55-57
 *
 * So posting FormData through `api.post` does not send a file at all. It sends
 * a JSON object shaped like the form, multer finds no multipart body, and the
 * route answers `400 No file provided` — a failure that names the one thing
 * that is not true.
 *
 * Naming `multipart/form-data` here defeats that branch. The boundary is not
 * ours to invent: the adapter deletes a boundary-less multipart header so the
 * browser can set the real one (axios/lib/adapters/fetch.js:409-415).
 *
 * This lives in one place because it is a property of the shared client, not
 * of any screen. Every call site that reconstructs it is one that can get it
 * wrong.
 */
export async function apiUpload<T>(
  url: string,
  form: FormData,
): Promise<ApiResponse<T>> {
  return apiRequest<T>('POST', url, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/**
 * Download a file the API produces and hand it to the browser (a CSV export,
 * 2026-09-21). `apiDownload` above returns the bytes for a caller that renders
 * them; this one saves them, so the two are separate functions rather than a
 * flag: they differ in what the caller does next, not in how they fetch.
 *
 * It goes through the shared client so the request carries the session the
 * same way every other one does — a plain `window.open` on the URL would
 * arrive without the bearer token and be refused. The response is a blob;
 * the filename is the one the server chose, read from Content-Disposition,
 * because the server knows what the file is and the screen does not.
 *
 * Resolves to an error message for the caller to show, or null on success.
 */
export async function apiDownloadFile(url: string, params?: Record<string, string | null | undefined>): Promise<string | null> {
  try {
    const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''));
    const response = await apiClient.request<Blob>({ method: 'GET', url, params: clean, responseType: 'blob' });
    const disposition = String(response.headers['content-disposition'] || '');
    const match = disposition.match(/filename="?([^"]+)"?/);
    const name = match ? match[1] : 'export.csv';
    const href = window.URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick: Safari has not finished reading it synchronously.
    window.setTimeout(() => window.URL.revokeObjectURL(href), 0);
    return null;
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 403) return 'You do not have permission to export this list.';
    if (status === 404) return 'That export is not available on this environment.';
    return 'Could not download the file. Try again in a moment.';
  }
}

// Generic paginated API function
async function paginatedApiRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<PaginatedResponse<T>> {
  try {
    const response = await apiClient.request<PaginatedResponse<T>>({
      method,
      url,
      data,
      ...config,
    });
    return response.data;
  } catch (error: any) {
    // Return a structured error response with default pagination.
    //
    // This is the same contract as `apiRequest` above and must stay that way;
    // it was previously the same defect copied, and it is the more dangerous
    // copy: the `data: []` below means a refused or failed page is structurally
    // identical to a genuinely empty one, so callers that trust `data.length`
    // render "nothing here" for a failure. Preserving `status` and `code` is
    // what lets them tell the two apart. See the note in `apiRequest` for why
    // `code` comes from `data.code` and never from `data.error`.
    return {
      success: false,
      data: [],
      error: error.response?.data?.message || error.message || 'Request failed',
      message: error.response?.data?.message || 'Request failed',
      status: error.response?.status,
      code: error.response?.data?.code,
      detail: error.response?.data?.detail,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      }
    };
  }
}

// Authentication API
export const authApi = {
  // Login user
  login: async (credentials: LoginForm): Promise<ApiResponse<{ user: User; token: string }>> => {
    return apiRequest('POST', API_ENDPOINTS.auth.login, credentials);
  },

  // Register user - bulletproof version that only sends required fields
  register: async (userData: {
    email: string;
    password: string;
    confirmPassword: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    businessName?: string;
    businessAddress?: string;
    accreditationStatus?: string;
    accreditationCriteria?: string[];
    kyc?: any;
    profile?: any;
  }): Promise<any> => {
    // Send all fields that backend validation expects
    const cleanData: Record<string, any> = {
      email: userData.email?.toLowerCase().trim() || '',
      password: userData.password,
      confirmPassword: userData.confirmPassword,
    };
    // Forward optional fields only if provided
    if (userData.firstName) cleanData.firstName = userData.firstName.trim();
    if (userData.lastName) cleanData.lastName = userData.lastName.trim();
    if (userData.phone) cleanData.phone = userData.phone.trim();
    if (userData.businessName) cleanData.businessName = userData.businessName.trim();
    if (userData.businessAddress) cleanData.businessAddress = userData.businessAddress.trim();
    if (userData.accreditationStatus) cleanData.accreditationStatus = userData.accreditationStatus;
    if (userData.accreditationCriteria) cleanData.accreditationCriteria = userData.accreditationCriteria;
    
    try {
      const response = await apiClient.request({
        method: 'POST',
        url: API_ENDPOINTS.auth.register,
        data: cleanData,
      });
      
      // Backend returns { message, user, token, expiresAt } directly
      return response.data; // Return as-is from backend
    } catch (error: any) {
      // Return a structured error response
      return {
        error: error.response?.data?.message || error.message || 'Request failed',
        message: error.response?.data?.message || error.message || 'Registration failed',
      };
    }
  },

  forgotPassword: async (email: string): Promise<ApiResponse> => {
    return apiRequest('POST', API_ENDPOINTS.auth.forgotPassword, { email });
  },

  resetPassword: async (data: { token: string; password: string; confirmPassword: string }): Promise<ApiResponse> => {
    return apiRequest('POST', API_ENDPOINTS.auth.resetPassword, data);
  },

  // Logout user
  logout: async (): Promise<ApiResponse> => {
    return apiRequest('POST', API_ENDPOINTS.auth.logout);
  },

  // Refresh token
  refreshToken: async (): Promise<ApiResponse<{ token: string }>> => {
    return apiRequest('POST', API_ENDPOINTS.auth.refresh);
  },

  // Connect wallet
  connectWallet: async (address: string): Promise<ApiResponse> => {
    return apiRequest('POST', API_ENDPOINTS.auth.walletConnect, { address });
  },

  // Initiate KYC
  initiateKyc: async (): Promise<ApiResponse<{ kycUrl: string }>> => {
    return apiRequest('POST', API_ENDPOINTS.auth.kycInitiate);
  },

  // Get user profile (uses /auth/me which returns { success, data } format)
  getProfile: async (): Promise<ApiResponse<User>> => {
    return apiRequest('GET', '/auth/me');
  },

  // Update user profile
  updateProfile: async (data: Partial<User>): Promise<ApiResponse<User>> => {
    return apiRequest('PUT', API_ENDPOINTS.users.updateProfile, data);
  },

  // Get onboarding status
  // TODO: Wire in production API for account progress status -mp 
  getOnboardingStatus: async (): Promise<ApiResponse<any>> => {
    return apiRequest('GET', `${config.apiBaseUrl}/user/onboarding-status`);
  },
};

// Properties API
export const propertiesApi = {
  // Get properties list with filters
  getProperties: async (filters?: PropertyFilters): Promise<PaginatedResponse<Property>> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    
    const url = `${API_ENDPOINTS.properties.list}?${params.toString()}`;
    return paginatedApiRequest('GET', url);
  },

  // Get single property
  getProperty: async (id: string): Promise<ApiResponse<Property>> => {
    return apiRequest('GET', API_ENDPOINTS.properties.get(id));
  },

  // Create property (admin only)
  createProperty: async (data: Partial<Property>): Promise<ApiResponse<Property>> => {
    return apiRequest('POST', API_ENDPOINTS.properties.create, data);
  },

  // Update property (admin only)
  updateProperty: async (id: string, data: Partial<Property>): Promise<ApiResponse<Property>> => {
    return apiRequest('PUT', API_ENDPOINTS.properties.update(id), data);
  },

  // Upload property image
  uploadImage: async (id: string, file: File): Promise<ApiResponse<{ imageUrl: string }>> => {
    const formData = new FormData();
    formData.append('image', file);
    
    return apiRequest('POST', API_ENDPOINTS.properties.uploadImage(id), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Transactions API
export const transactionsApi = {
  // Get transaction history
  getTransactions: async (filters?: TransactionFilters): Promise<PaginatedResponse<Transaction>> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    
    const url = `${API_ENDPOINTS.transactions.list}?${params.toString()}`;
    return paginatedApiRequest('GET', url);
  },

  // Buy property shares
  buyShares: async (data: {
    property_id: string;
    shares: number;
    price_per_share: number;
  }): Promise<ApiResponse<Transaction>> => {
    return apiRequest('POST', API_ENDPOINTS.transactions.buy, data);
  },

  // Sell property shares
  sellShares: async (data: {
    property_id: string;
    shares: number;
    price_per_share: number;
  }): Promise<ApiResponse<Transaction>> => {
    return apiRequest('POST', API_ENDPOINTS.transactions.sell, data);
  },

  // Get transaction history with detailed info
  getTransactionHistory: async (): Promise<ApiResponse<Transaction[]>> => {
    return apiRequest('GET', API_ENDPOINTS.transactions.history);
  },
};

// Staking API
export const stakingApi = {
  // Get user's portfolio
  getPortfolio: async (): Promise<ApiResponse<Portfolio>> => {
    return apiRequest('GET', API_ENDPOINTS.staking.portfolio);
  },

  // Stake shares
  stakeShares: async (data: {
    property_id: string;
    shares: number;
    lock_period_days?: number;
  }): Promise<ApiResponse<StakingPosition>> => {
    return apiRequest('POST', API_ENDPOINTS.staking.stake, data);
  },

  // Unstake shares
  unstakeShares: async (stakingId: string): Promise<ApiResponse> => {
    return apiRequest('POST', API_ENDPOINTS.staking.unstake(stakingId));
  },

  // Claim dividends
  claimDividends: async (): Promise<ApiResponse<{ amount: number; transaction_id: string }>> => {
    return apiRequest('POST', API_ENDPOINTS.staking.claim);
  },
};

// Admin API (admin only)
export const adminApi = {
  // Dashboard Metrics
  getDashboardMetrics: async (): Promise<ApiResponse<any>> => {
    return apiRequest('GET', '/admin/dashboard');
  },

  // Get system health
  getSystemHealth: async (): Promise<ApiResponse<any>> => {
    return apiRequest('GET', '/health');
  },

  // Get platform metrics
  getMetrics: async (): Promise<ApiResponse<{
    total_users: number;
    total_properties: number;
    total_volume: number;
    total_staked: number;
    recent_transactions: Transaction[];
  }>> => {
    return apiRequest('GET', API_ENDPOINTS.admin.metrics);
  },

  // Property Management
  getProperties: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    verification_status?: string;
  }): Promise<ApiResponse<Property[]>> => {
    return apiRequest('GET', API_ENDPOINTS.admin.properties, undefined, { params });
  },

  getProperty: async (id: string): Promise<ApiResponse<Property>> => {
    return apiRequest('GET', API_ENDPOINTS.properties.get(id));
  },

  updateProperty: async (id: string, data: Partial<Property>): Promise<ApiResponse<Property>> => {
    return apiRequest('PUT', API_ENDPOINTS.properties.update(id), data);
  },

  updatePropertyVerification: async (
    id: string, 
    status: 'pending' | 'verified' | 'rejected',
    notes?: string
  ): Promise<ApiResponse<Property>> => {
    return apiRequest('PATCH', `/properties/${id}/verification`, {
      verification_status: status,
      verification_notes: notes
    });
  },

  // Property Upload
  uploadPropertiesCSV: async (formData: FormData): Promise<ApiResponse<any>> => {
    return apiRequest('POST', API_ENDPOINTS.admin.uploadProperties, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes for file upload
    });
  },

  getUploadBatches: async (): Promise<ApiResponse<any[]>> => {
    return apiRequest('GET', '/properties/upload-batches');
  },

  // User Management
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    role?: string;
    kyc_status?: string;
  }): Promise<ApiResponse<User[]>> => {
    return apiRequest('GET', API_ENDPOINTS.admin.users, undefined, { params });
  },

  getUser: async (id: string): Promise<ApiResponse<User>> => {
    return apiRequest('GET', `/admin/users/${id}`);
  },

  updateUser: async (id: string, data: Partial<User>): Promise<ApiResponse<User>> => {
    return apiRequest('PUT', `/admin/users/${id}`, data);
  },

  updateUserKYC: async (
    id: string, 
    status: 'pending' | 'verified' | 'rejected',
    notes?: string
  ): Promise<ApiResponse<User>> => {
    return apiRequest('PATCH', `/admin/users/${id}/kyc`, {
      kyc_status: status,
      kyc_notes: notes
    });
  },

  // Analytics
  getVolumeAnalytics: async (timeframe: string = '24h'): Promise<ApiResponse<any>> => {
    return apiRequest('GET', `/transactions/volume?timeframe=${timeframe}`);
  },

  getPropertyAnalytics: async (id: string): Promise<ApiResponse<any>> => {
    return apiRequest('GET', `/properties/${id}/analytics`);
  },

  getSharesAnalytics: async (propertyId?: string): Promise<ApiResponse<any>> => {
    const url = propertyId 
      ? `/properties/${propertyId}/shares-monitoring`
      : '/properties/shares-monitoring';
    return apiRequest('GET', url);
  },

  getYieldAnalytics: async (propertyId?: string): Promise<ApiResponse<any>> => {
    const url = propertyId 
      ? `/properties/${propertyId}/yield-calculation`
      : '/properties/yield-calculation';
    return apiRequest('GET', url);
  },

  // Wallet Management
  getUserWallets: async (userId?: string): Promise<ApiResponse<any[]>> => {
    const url = userId ? `/wallets/user/${userId}` : '/wallets';
    return apiRequest('GET', url);
  },

  createManagedWallet: async (userId: string): Promise<ApiResponse<any>> => {
    return apiRequest('POST', '/wallets/managed', { userId });
  },

  linkExternalWallet: async (userId: string, address: string): Promise<ApiResponse<any>> => {
    return apiRequest('POST', '/wallets/external', { 
      userId, 
      address 
    });
  },

  // Additional wallet management methods
  getWalletPrivateKey: async (walletId: string): Promise<ApiResponse<any>> => {
    return apiRequest('GET', `/wallets/${walletId}/private-key`);
  },

  refreshWalletBalance: async (walletId: string): Promise<ApiResponse<any>> => {
    return apiRequest('POST', `/wallets/${walletId}/refresh-balance`);
  },

  deleteWallet: async (walletId: string): Promise<ApiResponse<any>> => {
    return apiRequest('DELETE', `/wallets/${walletId}`);
  },

  // Financial Operations
  addFunds: async (data: {
    amount: number;
    payment_method?: string;
  }): Promise<ApiResponse<any>> => {
    return apiRequest('POST', '/bank/deposit', {
      amountCents: Math.round(data.amount * 100),
      bankAccountId: data.payment_method || null
    });
  },

  withdrawFunds: async (data: {
    amount: number;
    bankAccountId: string;
  }): Promise<ApiResponse<any>> => {
    return apiRequest('POST', '/bank/withdraw', {
      amountCents: Math.round(data.amount * 100),
      bankAccountId: data.bankAccountId
    });
  },

  stakeFunds: async (data: {
    contractId: string;
    amount: number;
  }): Promise<ApiResponse<any>> => {
    return apiRequest('POST', '/staking/stake', {
      propertyId: data.contractId,
      amount: data.amount,
      shares: Math.floor(data.amount / 100) // Estimate shares based on amount
    });
  },

  unstakeFunds: async (data: {
    contractId: string;
    amount: number;
  }): Promise<ApiResponse<any>> => {
    return apiRequest('POST', '/staking/unstake', {
      stakingId: data.contractId,
      amount: data.amount
    });
  },

  claimStakingRewards: async (data: {
    contractId: string;
  }): Promise<ApiResponse<any>> => {
    return apiRequest('POST', '/staking/claim', {
      stakingId: data.contractId
    });
  },

  // Get bank accounts
  getBankAccounts: async (): Promise<ApiResponse<any>> => {
    return apiRequest('GET', '/bank/accounts');
  },

  // Get staking contracts
  getStakingContracts: async (): Promise<ApiResponse<any>> => {
    return apiRequest('GET', '/staking');
  },

  // Transactions
  getTransactions: async (params?: {
    page?: number;
    limit?: number;
    user_id?: string;
    property_id?: string;
    transaction_type?: string;
    status?: string;
  }): Promise<ApiResponse<{ data: Transaction[]; pagination: any }>> => {
    return apiRequest('GET', API_ENDPOINTS.transactions.list, undefined, { params });
  },

  getTransaction: async (id: string): Promise<ApiResponse<Transaction>> => {
    return apiRequest('GET', `/transactions/${id}`);
  },

  updateTransactionStatus: async (
    id: string, 
    status: 'pending' | 'confirmed' | 'failed' | 'cancelled'
  ): Promise<ApiResponse<Transaction>> => {
    return apiRequest('PATCH', `/transactions/${id}/status`, { status });
  },

  getAuditLogs: async (params?: {
    page?: number;
    limit?: number;
    action?: string;
    user_id?: string;
  }): Promise<ApiResponse<any[]>> => {
    return apiRequest('GET', API_ENDPOINTS.admin.auditLogs, undefined, { params });
  },

  // Export Functions
  exportUsers: async (format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/admin/export/users?format=${format}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      console.error('Export users error:', error);
      throw new Error(error.response?.data?.message || 'Failed to export users');
    }
  },

  exportProperties: async (format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/admin/export/properties?format=${format}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      console.error('Export properties error:', error);
      throw new Error(error.response?.data?.message || 'Failed to export properties');
    }
  },

  exportTransactions: async (format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/admin/export/transactions?format=${format}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      console.error('Export transactions error:', error);
      throw new Error(error.response?.data?.message || 'Failed to export transactions');
    }
  },

  // Get all users (admin only) - backward compatibility
  getAdminUsers: async (): Promise<PaginatedResponse<User>> => {
    return paginatedApiRequest('GET', API_ENDPOINTS.admin.users);
  },

  // Get all properties for admin - backward compatibility
  getAdminProperties: async (): Promise<PaginatedResponse<Property>> => {
    return paginatedApiRequest('GET', API_ENDPOINTS.admin.properties);
  },

  // Upload properties via CSV - backward compatibility
  uploadProperties: async (formData: FormData, onProgress?: (progress: number) => void): Promise<ApiResponse<any>> => {
    return apiRequest('POST', API_ENDPOINTS.admin.uploadProperties, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  },
};

// WebSocket connection management
class WebSocketManager {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectInterval = 5000;
  private eventListeners: Map<string, ((data: any) => void)[]> = new Map();

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const token = localStorage.getItem('authToken');
        const wsUrl = `${config.wsUrl}?token=${token}`;
        
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onclose = () => {
          console.log('WebSocket disconnected');
          this.attemptReconnect();
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting WebSocket reconnection (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.connect().catch(console.error);
      }, this.reconnectInterval);
    }
  }

  private handleMessage(message: any): void {
    const { type, data } = message;
    const listeners = this.eventListeners.get(type) || [];
    listeners.forEach(listener => listener(data));
  }

  subscribe(eventType: string, callback: (data: any) => void): () => void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    
    this.eventListeners.get(eventType)!.push(callback);

    // Return unsubscribe function
    return () => {
      const listeners = this.eventListeners.get(eventType);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  send(type: string, data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, data }));
    }
  }
}

// Export WebSocket instance
export const wsManager = new WebSocketManager();

// Utility functions
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

export const formatPercentage = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
};

export const formatDate = (date: string): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(date));
};

export const formatDateTime = (date: string): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
};

// Portfolio API
export const portfolioApi = {
    // Get portfolio summary
  getSummary: async (): Promise<ApiResponse<Portfolio>> => {
    return apiRequest('GET', API_ENDPOINTS.portfolio.summary);
  },

  // Get user positions
  getPositions: async (): Promise<ApiResponse<PortfolioPosition[]>> => {
    return apiRequest('GET', API_ENDPOINTS.portfolio.positions);
  },

  // Get portfolio performance
  getPerformance: async (period?: string): Promise<ApiResponse<any>> => {
    const params = period ? `?period=${period}` : '';
    return apiRequest('GET', `${API_ENDPOINTS.portfolio.performance}${params}`);
  },
};

// Default export with common API utilities
const apiDefaults = {
  get: apiRequest.bind(null, 'GET'),
  post: apiRequest.bind(null, 'POST'),
  put: apiRequest.bind(null, 'PUT'),
  delete: apiRequest.bind(null, 'DELETE'),
  patch: apiRequest.bind(null, 'PATCH'),
};

export default apiDefaults;

// Named export as 'api' for convenience
export const api = {
  get: apiRequest.bind(null, 'GET'),
  post: apiRequest.bind(null, 'POST'),
  put: apiRequest.bind(null, 'PUT'),
  delete: apiRequest.bind(null, 'DELETE'),
  patch: apiRequest.bind(null, 'PATCH'),
};

// Wallet API
export const walletApi = {
  getSummary: () => 
    api.get('/api/v1/wallet/summary'),

  getTransactions: (params?: { limit?: number; type?: string; offset?: number }) => 
    api.get('/api/v1/wallet/transactions', { params }),

  getBalance: () =>
    api.get('/api/v1/wallet/balance'),

  addFunds: (data: { amount: number; paymentMethodId: string }) =>
    api.post('/api/v1/wallet/add-funds', data),

  withdraw: (data: { amount: number; bankAccountId: string }) =>
    api.post('/api/v1/wallet/withdraw', data),
};

// Bank API for ACH funding (legacy - use plaidApi for new integrations)
export const bankApi = {
  // Create a bank link session (deprecated - use plaidApi.createLinkToken)
  createLinkSession: async () => {
    return apiClient.post<ApiResponse<{ client_secret: string; session_id: string }>>(
      '/bank/link-session'
    );
  },
  
  // Get user's linked bank accounts
  getAccounts: async () => {
    return apiClient.get<ApiResponse<{ accounts: any[] }>>('/bank/accounts');
  },
  
  // Get real-time balance for a specific account
  getAccountBalance: async (accountId: string) => {
    return apiClient.get<ApiResponse<{ 
      balance: {
        cash?: { available?: Array<{ amount: number; currency: string }> };
        current?: { available?: Array<{ amount: number; currency: string }> };
      };
      lastRefreshed: string;
    }>>(`/bank/accounts/${accountId}/balance`);
  },
  
  // Get balances for ALL user's bank accounts in one request (batch endpoint)
  // More efficient than calling getAccountBalance() N times
  getAllBalances: async () => {
    return apiClient.get<ApiResponse<{
      accounts: Array<{
        accountId: string;
        institutionName: string;
        last4: string;
        accountType: string;
        balance: {
          cash?: { available?: Array<{ amount: number; currency: string }> };
          current?: { available?: Array<{ amount: number; currency: string }> };
        } | null;
        lastRefreshed: string | null;
        success: boolean;
        error?: string;
      }>;
      totalAccounts: number;
      successfulFetches: number;
      lastRefreshed: string;
    }>>('/bank/balances');
  },
  
  // Confirm linked accounts after FC flow
  confirmSession: async (sessionId: string) => {
    return apiClient.post<ApiResponse<{ linked: number; accounts: any[] }>>(
      '/bank/confirm',
      { sessionId }
    );
  },
  
  // Get user's cash balance (not used for individual account balances)
  getBalance: async () => {
    return apiClient.get<ApiResponse<{ balanceCents: number }>>('/funding/balance');
  },
  
  // Initiate ACH transfer (deposit/withdrawal)
  transfer: async (data: { bank_account_id: string; amount_cents: number; direction: string }) => {
    return apiClient.post<ApiResponse<{ intent_id: string; status: string }>>('/bank/deposit', data);
  },
  
  // Remove a linked bank account
  removeAccount: async (accountId: string) => {
    return apiClient.delete<ApiResponse<{ message: string }>>(`/bank/accounts/${accountId}`);
  },
};

// Plaid API for bank account linking
export const plaidApi = {
  // Create a Plaid Link token for initiating bank connection
  createLinkToken: async () => {
    return apiClient.post<ApiResponse<{ link_token: string; expiration: string }>>(
      '/plaid/create-link-token'
    );
  },
  
  // Exchange public token for access token after successful Plaid Link
  exchangeToken: async (data: {
    public_token: string;
    accounts?: any[];
    institution?: any;
  }) => {
    return apiClient.post<ApiResponse<{
      linked: number;
      accounts: any[];
      institution: any;
    }>>('/plaid/exchange-token', data);
  },
  
  // Get user's Plaid-linked bank accounts
  getAccounts: async () => {
    return apiClient.get<ApiResponse<{ accounts: any[] }>>('/plaid/accounts');
  },
  
  // Get real-time balance for a Plaid account
  getAccountBalance: async (accountId: string) => {
    return apiClient.get<ApiResponse<{
      balance: {
        available: number | null;
        current: number | null;
        currency: string;
      };
      lastRefreshed: string;
    }>>(`/plaid/accounts/${accountId}/balance`);
  },
  
  // Remove a linked Plaid account
  removeAccount: async (accountId: string) => {
    return apiClient.delete<ApiResponse<{ message: string }>>(`/plaid/accounts/${accountId}`);
  },
  
  // Reserved for future ACH processor integration
  // createAchToken: async (accountId: string) => {
  //   return apiClient.post<ApiResponse<{ ach_token: string }>>(
  //     '/plaid/ach-token',
  //     { accountId }
  //   );
  // },
  
  // Get balances for ALL user's Plaid accounts in one request (batch endpoint)
  getAllBalances: async () => {
    return apiClient.get<ApiResponse<{
      accounts: Array<{
        accountId: string;
        plaidAccountId: string;
        institutionName: string;
        accountName: string;
        last4: string;
        accountType: string;
        subtype: string;
        balance: {
          available: number | null;
          current: number | null;
          currency: string;
        } | null;
        lastRefreshed: string | null;
        success: boolean;
        error?: string;
      }>;
      totalAccounts: number;
      successfulFetches: number;
      lastRefreshed: string;
    }>>('/plaid/balances');
  },
  
  // Check Plaid integration health
  healthCheck: async () => {
    return apiClient.get<ApiResponse<{ configured: boolean; environment: string }>>(
      '/plaid/health'
    );
  },
  
  // ===== Plaid IDV (Identity Verification) for KYC =====
  
  // Create a Plaid IDV session for identity verification
  createIdvSession: async () => {
    return apiClient.post<ApiResponse<{
      sessionId: string;
      url: string;
      status: string;
      expiration?: string;
    } | { alreadyVerified: true; message: string; status: string }>>('/plaid/idv/session');
  },
  
  // Get IDV status
  getIdvStatus: async () => {
    return apiClient.get<ApiResponse<{
      sessionId?: string;
      status: string;
      plaidStatus?: string;
      steps?: any;
      createdAt?: string;
      message?: string;
    }>>('/plaid/idv/status');
  },
  
  // Retry a failed IDV session
  retryIdv: async () => {
    return apiClient.post<ApiResponse<{
      sessionId: string;
      url: string;
      status: string;
    }>>('/plaid/idv/retry');
  },
};

// Hive Fund API for investment management
export const hiveFundApi = {
  // Get Hive Fund information (public)
  getInfo: async (): Promise<ApiResponse<{
    fundName: string;
    totalRaised: number;
    totalInvestors: number;
    targetRaise: number;
    currentApy: number;
    distributionFrequency: string;
    fundStatus: string;
    inceptionDate: string;
    nextDistributionDate: string;
    percentFunded: string;
  }>> => {
    return apiRequest('GET', '/hivefund/info');
  },

  // Create new investment
  invest: async (data: {
    amount: number;
    paymentMethod: 'bank_ach' | 'crypto' | 'wire' | 'investment_account';
    bankAccountId?: string;
    cryptoWalletAddress?: string;
    agreementAccepted: boolean;
    legal_acceptances?: Record<string, boolean>;
    signatureImage?: string;
    subscriptionData?: SubscriptionData;
  }): Promise<ApiResponse<{
    investmentId: string;
    confirmationCode: string;
    amount: number;
    paymentMethod: string;
    status: string;
    paymentIntentId?: string;
    investedAt: string;
    estimatedCompletion: string;
    coinbase?: { hostedUrl: string; chargeId: string; chargeCode: string; expiresAt: string };
    wireInstructions?: {
      bankName?: string;
      accountName?: string;
      accountNumber?: string;
      routingNumber?: string;
      swiftCode?: string;
      reference?: string;
      expiresAt?: string;
    };
  }>> => {
    return apiRequest('POST', '/hivefund/invest', data);
  },

  // Get user's investment position
  getPosition: async (): Promise<ApiResponse<{
    hasInvestment: boolean;
    totalInvested: number;
    totalInvestedCents: number;
    activeInvestmentCount: number;
    currentApy: number;
    estimatedQuarterlyDistribution: number;
    nextDistributionDate: string;
    totalDistributionsReceived: number;
    investments: Array<{
      id: string;
      amount: number;
      paymentMethod: string;
      status: string;
      confirmationCode: string;
      sharesAllocated: number;
      investedAt: string;
      activatedAt: string | null;
    }>;
    distributionHistory: Array<{
      amount: number;
      period: string;
      date: string;
      status: string;
      paidAt: string | null;
    }>;
  }>> => {
    return apiRequest('GET', '/hivefund/position');
  },

  // Get distribution history
  getDistributions: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{
    distributions: Array<{
      id: string;
      amount: number;
      period: string;
      date: string;
      status: string;
      paidAt: string | null;
      confirmationCode: string;
      investmentAmount: number;
    }>;
    totalDistributions: number;
    totalPaid: number;
    pagination: {
      limit: number;
      offset: number;
      total: number;
    };
  }>> => {
    return apiRequest('GET', '/hivefund/distributions', undefined, { params });
  },

  // Get specific investment details
  getInvestment: async (investmentId: string): Promise<ApiResponse<{
    id: string;
    amount: number;
    paymentMethod: string;
    status: string;
    confirmationCode: string;
    sharesAllocated: number;
    investedAt: string;
    activatedAt: string | null;
    failedReason: string | null;
    paymentIntentId: string | null;
    activityLog: Array<{
      type: string;
      description: string;
      timestamp: string;
      metadata: any;
    }>;
  }>> => {
    return apiRequest('GET', `/hivefund/investment/${investmentId}`);
  },
};

// ═════════════════════════════════════════════════════════════════════════════
// Crypto Funding API - Onramp/Offramp, Wallets, Balances
// ═════════════════════════════════════════════════════════════════════════════

export interface CryptoWallet {
  id: string;
  wallet_address: string;
  wallet_type: 'managed' | 'external';
  network: string;
  purpose: string;
  label?: string;
  is_primary: boolean;
  is_active: boolean;
  created_at: string;
  balance_usdc?: string;
}

export interface CryptoBalance {
  asset: string;
  network: string;
  balance: string;
  balance_display: number;
}

export interface RampTransaction {
  id: string;
  direction: 'onramp' | 'offramp';
  status: string;
  fiat_currency: string;
  fiat_amount: number;
  fiat_fee: number;
  crypto_asset: string;
  crypto_amount_display: number;
  network: string;
  user_wallet_address: string;
  platform_wallet_address: string;
  blockchain_tx_hash: string | null;
  ach_transfer_id: string | null;
  exchange_rate: number;
  created_at: string;
  updated_at: string;
  settled_at: string | null;
}

export interface LedgerEntry {
  id: string;
  asset: string;
  network: string;
  entry_type: string;
  amount_display: number;
  balance_after: string;
  description: string;
  created_at: string;
}

export const cryptoApi = {
  // ── Wallets ─────────────────────────────────────────────
  getWallets: async (): Promise<ApiResponse<CryptoWallet[]>> => {
    return apiRequest('GET', '/crypto/wallets');
  },

  getWallet: async (id: string): Promise<ApiResponse<CryptoWallet>> => {
    return apiRequest('GET', `/crypto/wallets/${id}`);
  },

  createManagedWallet: async (data: {
    network?: string;
    purpose?: string;
  }): Promise<ApiResponse<CryptoWallet>> => {
    return apiRequest('POST', '/crypto/wallets/managed', data);
  },

  registerExternalWallet: async (data: {
    address: string;
    network?: string;
    purpose?: string;
  }): Promise<ApiResponse<CryptoWallet>> => {
    return apiRequest('POST', '/crypto/wallets/external', data);
  },

  // ── Balances ────────────────────────────────────────────
  getBalances: async (): Promise<ApiResponse<CryptoBalance[]>> => {
    return apiRequest('GET', '/crypto/balance');
  },

  getAssetBalance: async (
    asset: string,
    network?: string
  ): Promise<ApiResponse<CryptoBalance>> => {
    return apiRequest('GET', `/crypto/balance/${asset}`, undefined, {
      params: { network },
    });
  },

  // ── Onramp (Buy) ───────────────────────────────────────
  initiateOnramp: async (data: {
    amountUsd: number;
    network?: string;
    userWalletAddress?: string;
    externalAccountId: string;
  }): Promise<ApiResponse<RampTransaction>> => {
    return apiRequest('POST', '/crypto/onramp', data);
  },

  // ── Offramp (Sell) ──────────────────────────────────────
  initiateOfframp: async (data: {
    amountUsd: number;
    network?: string;
    externalAccountId: string;
  }): Promise<ApiResponse<RampTransaction>> => {
    return apiRequest('POST', '/crypto/offramp', data);
  },

  // ── Transactions ────────────────────────────────────────
  getTransactions: async (params?: {
    direction?: 'onramp' | 'offramp';
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<RampTransaction[]>> => {
    return apiRequest('GET', '/crypto/transactions', undefined, { params });
  },

  getTransaction: async (id: string): Promise<ApiResponse<RampTransaction>> => {
    return apiRequest('GET', `/crypto/transactions/${id}`);
  },

  // ── Payment ─────────────────────────────────────────────
  verifyPayment: async (data: {
    chain: string;
    txHash: string;
    toAddress?: string;
    expectedAmountUsd?: number;
  }): Promise<ApiResponse<any>> => {
    return apiRequest('POST', '/crypto/verify-payment', data);
  },

  getPaymentInstructions: async (params: {
    chain?: string;
    amount: number;
    memo?: string;
  }): Promise<ApiResponse<any>> => {
    return apiRequest('GET', '/crypto/payment-instructions', undefined, { params });
  },

  // ── Ledger ──────────────────────────────────────────────
  getLedgerHistory: async (params?: {
    asset?: string;
    network?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<LedgerEntry[]>> => {
    return apiRequest('GET', '/crypto/ledger', undefined, { params });
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Share Conversion API — USD → Crypto Custody (ETH / XRP / BTC)
// ═══════════════════════════════════════════════════════════════════════════

export interface CryptoPosition {
  id: string;
  tenantId: string;
  userId: string;
  asset: 'ETH' | 'XRP' | 'BTC';
  cryptoAmount: number;
  costBasisUsd: number;
  costBasisRate: number;
  status: 'active' | 'cashing_out' | 'settled' | 'cancelled';
  currentPrice: number | null;
  currentValue: number | null;
  pnl: number | null;
  pnlPercent: number | null;
  cashout: {
    requestedAt: string;
    rate: number | null;
    usd: number | null;
    feeUsd: number | null;
    achTransferId: string | null;
    settledAt: string | null;
  } | null;
  platformWalletId: string | null;
  custodyTxHash: string | null;
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface PortfolioAsset {
  asset: string;
  positionCount: number;
  totalCrypto: number;
  totalInvested: number;
  currentPrice: number;
  currentValue: number;
  pnl: number;
  pnlPercent: number;
}

export interface PortfolioSummary {
  assets: Record<string, PortfolioAsset>;
  summary: {
    totalInvested: number;
    totalCurrentValue: number;
    totalPnl: number;
    totalPnlPercent: number;
    assetCount: number;
  };
}

export interface PriceData {
  prices: Record<string, number | null>;
  supportedAssets: string[];
  minConversionUsd: number;
  maxConversionUsd: number;
}

export const shareConversionApi = {
  // ── Prices ──────────────────────────────────────────────
  getPrices: async (): Promise<ApiResponse<PriceData>> => {
    return apiRequest('GET', '/share-conversion/prices');
  },

  getAssetPrice: async (asset: string): Promise<ApiResponse<{ asset: string; price: number }>> => {
    return apiRequest('GET', `/share-conversion/prices/${asset}`);
  },

  // ── Conversions ─────────────────────────────────────────
  convert: async (data: {
    amountUsd: number;
    asset: 'ETH' | 'XRP' | 'BTC';
  }): Promise<ApiResponse<CryptoPosition>> => {
    return apiRequest('POST', '/share-conversion/convert', data);
  },

  getPositions: async (params?: {
    asset?: string;
    status?: string;
  }): Promise<ApiResponse<CryptoPosition[]>> => {
    return apiRequest('GET', '/share-conversion/positions', undefined, { params });
  },

  getPosition: async (id: string): Promise<ApiResponse<CryptoPosition>> => {
    return apiRequest('GET', `/share-conversion/positions/${id}`);
  },

  getPortfolio: async (): Promise<ApiResponse<PortfolioSummary>> => {
    return apiRequest('GET', '/share-conversion/portfolio');
  },

  getHistory: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<CryptoPosition[]>> => {
    return apiRequest('GET', '/share-conversion/history', undefined, { params });
  },

  // ── Cash-out ────────────────────────────────────────────
  cashout: async (positionId: string, data: {
    externalAccountId: string;
  }): Promise<ApiResponse<CryptoPosition>> => {
    return apiRequest('POST', `/share-conversion/cashout/${positionId}`, data);
  },

  // ── Admin ───────────────────────────────────────────────
  adminListPositions: async (params?: {
    status?: string;
    asset?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<CryptoPosition[]>> => {
    return apiRequest('GET', '/share-conversion/admin/positions', undefined, { params });
  },

  adminGetStats: async (): Promise<ApiResponse<any>> => {
    return apiRequest('GET', '/share-conversion/admin/stats');
  },

  adminSettle: async (positionId: string): Promise<ApiResponse<CryptoPosition>> => {
    return apiRequest('POST', `/share-conversion/admin/settle/${positionId}`);
  },

  adminCancel: async (positionId: string, notes?: string): Promise<ApiResponse<CryptoPosition>> => {
    return apiRequest('POST', `/share-conversion/admin/cancel/${positionId}`, { notes });
  },

  // ── Convertible Value ───────────────────────────────────
  getConvertibleValue: async (): Promise<ApiResponse<any>> => {
    return apiRequest('GET', '/share-conversion/convertible-value');
  },

  // ── Liquidation (User) ─────────────────────────────────
  requestLiquidation: async (sourceId: string, data: {
    sourceType: 'crypto_position' | 'investment';
    externalAccountId: string;
    bankAccountId?: string;
  }): Promise<ApiResponse<any>> => {
    return apiRequest('POST', `/share-conversion/liquidate/${sourceId}`, data);
  },

  getLiquidationRequests: async (): Promise<ApiResponse<any[]>> => {
    return apiRequest('GET', '/share-conversion/liquidation-requests');
  },

  cancelLiquidation: async (requestId: string): Promise<ApiResponse<any>> => {
    return apiRequest('POST', `/share-conversion/liquidation-requests/${requestId}/cancel`);
  },

  // ── Admin Liquidation ──────────────────────────────────
  adminListLiquidations: async (params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<any[]>> => {
    return apiRequest('GET', '/share-conversion/admin/liquidation-requests', undefined, { params });
  },

  adminGetLiquidationStats: async (): Promise<ApiResponse<any>> => {
    return apiRequest('GET', '/share-conversion/admin/liquidation-stats');
  },

  adminApproveLiquidation: async (requestId: string): Promise<ApiResponse<any>> => {
    return apiRequest('POST', `/share-conversion/admin/liquidation/${requestId}/approve`);
  },

  adminRejectLiquidation: async (requestId: string, notes?: string): Promise<ApiResponse<any>> => {
    return apiRequest('POST', `/share-conversion/admin/liquidation/${requestId}/reject`, { notes });
  },

  adminInitiateAch: async (requestId: string): Promise<ApiResponse<any>> => {
    return apiRequest('POST', `/share-conversion/admin/liquidation/${requestId}/initiate-ach`);
  },

  adminSettleLiquidation: async (requestId: string): Promise<ApiResponse<any>> => {
    return apiRequest('POST', `/share-conversion/admin/liquidation/${requestId}/settle`);
  },
};

// ============== Deal Investment API (reusable for any deal) ==============

export interface DealInvestInfo {
  id: string;
  slug: string;
  type: string;
  title: string;
  summary: string;
  raiseTarget: number | null;
  minInvestment: number | null;
  raised: number;
  kpis: Record<string, any>;
  branding: Record<string, any>;
  settings: Record<string, any>;
  fundWalletAddress: string | null;
  acceptedMethods: string[];
}

export interface DealInvestResult {
  investmentId: string;
  confirmationCode: string;
  txnId: string;
  amount: number;
  method: string;
  status: string;
  dealSlug: string;
  dealTitle: string;
  createdAt: string;
  coinbase?: { hostedUrl: string; chargeId: string; chargeCode: string; expiresAt: string };
  wireInstructions?: {
    bankName?: string;
    accountName?: string;
    accountNumber?: string;
    routingNumber?: string;
    swiftCode?: string;
    reference?: string;
    expiresAt?: string;
  };
}

export interface InvestmentRecord {
  id: string;
  dealId: string;
  dealTitle: string;
  dealSlug: string;
  dealType: string;
  amountCents: number;
  amount: number;
  method: string;
  status: string;
  paymentRef: string | null;
  createdAt: string;
  fundedAt: string | null;
}

export interface InvestmentTxn {
  id: string;
  txnId: string;
  event: string;
  amountCents: number;
  amount: number;
  method: string;
  currency: string;
  status: string;
  paymentRef: string | null;
  walletAddress: string | null;
  feesCents: number;
  netAmountCents: number;
  settledAt: string | null;
  failedAt: string | null;
  createdAt: string;
}

export const dealInvestApi = {
  /** Get deal invest info (public) */
  getInfo: async (slug: string): Promise<ApiResponse<DealInvestInfo>> => {
    return apiRequest('GET', `/deals/${slug}/invest/info`);
  },

  /** Submit investment for a deal */
  invest: async (slug: string, data: {
    amount: number;
    method: 'ach' | 'wire' | 'crypto' | 'tao' | 'investment_account';
    bankAccountId?: string;
    walletAddress?: string;
    cryptoTxHash?: string;
    currency?: string;
    agreementAccepted: boolean;
    phone?: string;
    legal_acceptances?: Record<string, boolean>;
    signedDocIds?: string[];
    signatureImage?: string;
    subscriptionData?: SubscriptionData;
  }): Promise<ApiResponse<DealInvestResult>> => {
    return apiRequest('POST', `/deals/${slug}/invest`, data);
  },

  /** Get current user's investments across all deals */
  getMyInvestments: async (): Promise<ApiResponse<InvestmentRecord[]>> => {
    return apiRequest('GET', '/deals/investments/mine');
  },

  /** Get transaction history for an investment */
  getInvestmentTxns: async (investmentId: string): Promise<ApiResponse<InvestmentTxn[]>> => {
    return apiRequest('GET', `/deals/investments/${investmentId}/txns`);
  },
};

// ============== Subscription Data Types ==============

export interface SubscriptionData {
  vehicleType: 'individual' | 'entity' | 'trust' | 'ira';
  titleOption: string;
  titleDetails?: Record<string, string>;
  accreditedBases: string[];
  accreditedVerification?: string;
  riskAcknowledgments: Record<string, boolean>;
  investorInfo: {
    fullName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    ssn_last4?: string;
    dob?: string;
    entityName?: string;
    ein?: string;
    entityState?: string;
  };
  badActorAnswers: Record<string, boolean>;
  communityPropertyWaiver?: boolean;
  communityPropertyState?: string;
}

// ============== Subscriptions API ==============

export const subscriptionsApi = {
  /** Create a subscription agreement from wizard data */
  create: async (data: {
    dealId?: string;
    amountCents: number;
    unitCount?: number;
    unitClass?: string;
    titleOption: string;
    titleDetails: Record<string, any>;
    paymentMethod: string;
    investorSnapshot: Record<string, any>;
    badActorCheck: Record<string, any>;
    templateDocId?: string;
  }): Promise<ApiResponse<any>> => {
    return apiRequest('POST', '/subscriptions', data);
  },

  /** List my subscriptions */
  getMine: async (): Promise<ApiResponse<any[]>> => {
    return apiRequest('GET', '/subscriptions/mine');
  },

  /** Get subscription detail */
  get: async (id: string): Promise<ApiResponse<any>> => {
    return apiRequest('GET', `/subscriptions/${id}`);
  },

  /** Fill PDF form fields for a subscription */
  fill: async (id: string, additionalFields?: Record<string, string>): Promise<ApiResponse<any>> => {
    return apiRequest('POST', `/subscriptions/${id}/fill`, { additionalFields });
  },

  /** Sign a subscription (submit signature + form data) */
  sign: async (id: string, data: {
    signatureImage: string;
    initialsImage?: string;
    signerName: string;
    additionalFields?: Record<string, string>;
  }): Promise<ApiResponse<any>> => {
    return apiRequest('POST', `/subscriptions/${id}/sign`, data);
  },
};
